import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { ENV } from '../utils/env.js';
import { getIntegrationEndpoint, getFilterEndpoint } from '../config/integrationConfig';

/**
 * Hook for managing integration filters and filter options
 * @param {string} integrationType - The type of integration (s3, repositories, etc.)
 * @param {string} integrationId - The ID of the specific integration instance
 * @param {object} integrationConfig - The configuration object for this integration type
 */
export const useIntegrationFilters = (integrationType, integrationId, integrationConfig) => {
  // Filter options state (from API)
  const [filterOptions, setFilterOptions] = useState({
    counts: { active: 0, all: 0, ignored: 0, unvalidated: 0 },
    detector_types: [],
    total_count: 0,
  });

  // Filter state
  const [filterType, setFilterType] = useState('all'); // 'all', 'verified', 'unverified', 'ignored'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', or specific detector_type
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);

  // Filter handlers - Use useCallback for stability
  const handleFilterChange = useCallback((type) => {
    setFilterType(type);
  }, []);

  const handleSourceFilterChange = useCallback((source) => {
    setSourceFilter(source);
  }, []);

  // Fetch filter options from API
  const fetchFilterOptions = useCallback(async () => {
    if (!integrationConfig) return;

    // Try to get filter endpoint from config, fall back to default patterns
    const filterEndpoint = getFilterEndpoint(integrationType, { id: integrationId }) ||
                          getIntegrationEndpoint(integrationType, 'filterOptions', { id: integrationId }) ||
                          getIntegrationEndpoint(integrationType, 'counts', { id: integrationId }) ||
                          `api/${integrationType}-findings/filter-options/${integrationId}` ||
                          `api/findings/filter-options/${integrationId}`;
                          
    console.log(`[Filter Options] Attempting to fetch from: ${filterEndpoint}`);

    try {
      const response = await axios.get(
        `${ENV.API_URL}${filterEndpoint}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem('jwt')}`,
          },
        }
      );

      // Handle different response formats
      const data = response.data?.data || response.data;
      console.log(`[Filter Options] Response received:`, data);
      
      // Transform repository response format to expected format
      let transformedData = data;
      
      if (integrationType === 'github' && data.status && Array.isArray(data.status)) {
        // Repository format transformation
        const counts = { active: 0, all: 0, ignored: 0, unvalidated: 0 };
        
        // Transform status array to counts object
        data.status.forEach(([status, count]) => {
          if (status === 'active') counts.active = count;
          if (status === 'unvalidated') counts.unvalidated = count;
        });
        
        // Calculate totals
        counts.all = data.status_total || 0;
        counts.ignored = data.ignore_total || 0;
        
        // Transform detector_types from tuples to simple array
        const detector_types = Array.isArray(data.detector_types) 
          ? data.detector_types.map(([type, count]) => type)
          : [];
        
        transformedData = {
          counts: counts,
          detector_types: detector_types,
          total_count: data.status_total || 0,
        };
      }
      
      setFilterOptions({
        counts: transformedData.counts || { active: 0, all: 0, ignored: 0, unvalidated: 0 },
        detector_types: transformedData.detector_types || [],
        total_count: transformedData.total_count || 0,
      });
      
      console.log(`[Filter Options] Filter options updated:`, {
        counts: transformedData.counts || { active: 0, all: 0, ignored: 0, unvalidated: 0 },
        detector_types: transformedData.detector_types || [],
        total_count: transformedData.total_count || 0,
      });
    } catch (error) {
      console.error(`[Filter Options] Error fetching filter options from ${filterEndpoint}:`, error);
      console.error(`[Filter Options] Error details:`, error.response?.data || error.message);
      // Keep default filter options on error
    }
  }, [integrationType, integrationId, integrationConfig]);

  // Automatically fetch filter options when hook is initialized
  useEffect(() => {
    if (integrationType && integrationId && integrationConfig) {
      fetchFilterOptions();
    }
  }, [integrationType, integrationId, integrationConfig, fetchFilterOptions]);

  // Get unique sources from findings data (fallback when API filter options aren't available)
  const getUniqueSourcesFromData = useCallback((findingsData, currentFilterType) => {
    if (!findingsData || findingsData.length === 0) {
      return [];
    }

    let filteredFindings = findingsData;
    if (currentFilterType === 'verified') {
      filteredFindings = filteredFindings.filter(finding => finding.verified === true);
    } else if (currentFilterType === 'unverified') {
      filteredFindings = filteredFindings.filter(finding => finding.verified === false);
    } else if (currentFilterType === 'ignored') {
      filteredFindings = filteredFindings.filter(finding => finding.ignore_status === true || finding.ignore_status === 'true');
    }

    // Get unique detector types that have findings for the current status
    const uniqueSources = [...new Set(filteredFindings.map(finding => finding.detector_type))];
    return uniqueSources.sort();
  }, []);

  // Build filter request for API calls
  const buildFilterRequest = useCallback((additionalFilters = {}) => {
    const filters = { ...additionalFilters };
    
    if (filterType === 'verified' || filterType === 'unverified' || filterType === 'ignored') {
      const conditions = [];
      
      if (filterType === 'verified') {
        // Check if this integration type uses status mapping (like repositories)
        if (integrationConfig?.filterConfig?.statusMapping?.verified) {
          conditions.push({
            field: "status",
            operator: "eq",
            value: integrationConfig.filterConfig.statusMapping.verified
          });
        } else {
          conditions.push({
            field: "verified",
            operator: "eq",
            value: "true"
          });
        }
      } else if (filterType === 'unverified') {
        // Check if this integration type uses status mapping (like repositories)
        if (integrationConfig?.filterConfig?.statusMapping?.unverified) {
          conditions.push({
            field: "status", 
            operator: "eq",
            value: integrationConfig.filterConfig.statusMapping.unverified
          });
        } else {
          conditions.push({
            field: "verified",
            operator: "eq",
            value: "false"
          });
        }
      } else if (filterType === 'ignored') {
        // For ignored findings, use ignore_status field with boolean true
        conditions.push({
          field: "ignore_status",
          operator: "eq", 
          value: "true"
        });
      }
      
      // Include source filter in backend request if serverSideSourceFiltering is enabled
      if (sourceFilter !== 'all' && integrationConfig?.filterConfig?.serverSideSourceFiltering) {
        conditions.push({
          field: "detector_type",
          operator: "eq",
          value: sourceFilter
        });
      }
      
      if (conditions.length > 0) {
        filters.groups = [
          {
            conditions: conditions,
            operator: "AND"
          }
        ];
      }
    } else if (sourceFilter !== 'all' && integrationConfig?.filterConfig?.serverSideSourceFiltering) {
      filters.groups = [
        {
          conditions: [
            {
              field: "detector_type",
              operator: "eq",
              value: sourceFilter
            }
          ],
          operator: "AND"
        }
      ];
    }
    
    return filters;
  }, [filterType, sourceFilter, integrationConfig]);

  // Apply client-side filters to findings data
  const applyClientSideFilters = useCallback((findingsData) => {
    if (!findingsData || findingsData.length === 0) {
      return [];
    }

    let filtered = [...findingsData];

    // Apply verification filter
    if (filterType === 'verified') {
      // Check if this integration type uses status mapping (like repositories)
      if (integrationConfig?.filterConfig?.statusMapping?.verified) {
        // For repositories: verified means status === 'active'
        filtered = filtered.filter(finding => finding.verified === true);
      } else {
        filtered = filtered.filter(finding => finding.verified === true);
      }
    } else if (filterType === 'unverified') {
      // Check if this integration type uses status mapping (like repositories)
      if (integrationConfig?.filterConfig?.statusMapping?.unverified) {
        // For repositories: unverified means status === 'unvalidated'
        filtered = filtered.filter(finding => finding.verified === false);
      } else {
        filtered = filtered.filter(finding => finding.verified === false);
      }
    } else if (filterType === 'ignored') {
      // For ignored findings, filter by ignore_status or status='ignore'
      filtered = filtered.filter(finding => 
        finding.ignore_status === true || 
        finding.ignore_status === 'true' ||
        finding.status === 'ignore'
      );
    }

    // Apply source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(finding => finding.detector_type === sourceFilter);
    }

    return filtered;
  }, [filterType, sourceFilter, integrationConfig]);

  // Reset filters to default state
  const resetFilters = () => {
    setFilterType('all');
    setSourceFilter('all');
    setShowSourceDropdown(false);
  };

  // Get filter counts for display
  // Optionally accepts findings data to calculate counts from actual data
  // If allFindingsData is provided, it should be all findings (not paginated)
  const getFilterCounts = (findingsData = null) => {
    // If findings data is provided, calculate counts from data
    // Note: This should be all findings data, not just the current page
    if (findingsData && Array.isArray(findingsData)) {
      // For paginated data, we might need to calculate from all pages
      // But if we only have current page, calculate from it as approximation
      if (findingsData.length > 0) {
        const allCount = findingsData.length;
        const activeCount = findingsData.filter(f => f.verified === true).length;
        const unvalidatedCount = findingsData.filter(f => f.verified === false).length;
        const ignoredCount = findingsData.filter(f => f.status === 'ignore' || f.ignore_status === true || f.ignore_status === 'true').length;
        
        return {
          all: allCount,
          verified: activeCount,
          unverified: unvalidatedCount,
          ignored: ignoredCount
        };
      }
    }
    
    // Otherwise use filter options from API
    return {
      all: filterOptions.counts?.all || 0,
      verified: filterOptions.counts?.active || 0,
      unverified: filterOptions.counts?.unvalidated || 0,
      ignored: filterOptions.counts?.ignored || 0
    };
  };

  // Check if source filter is still valid for current filter type
  const validateSourceFilter = useCallback((availableSources) => {
    if (sourceFilter !== 'all' && !availableSources.includes(sourceFilter)) {
      setSourceFilter('all');
      return false;
    }
    return true;
  }, [sourceFilter]);

  // Get source count for a specific source in current filter context
  const getSourceCount = useCallback((source, filteredData) => {
    if (!filteredData || filteredData.length === 0) {
      return 0;
    }

    return filteredData.filter(finding => finding.detector_type === source).length;
  }, []);

  // Check if any filters are active (not default)
  const hasActiveFilters = () => {
    return filterType !== 'all' || sourceFilter !== 'all';
  };

  // Get filter summary for display
  const getFilterSummary = () => {
    const parts = [];
    
    if (filterType !== 'all') {
      const verifiedLabel = integrationConfig?.filterConfig?.filterLabels?.verified || 'Verified';
      const unverifiedLabel = integrationConfig?.filterConfig?.filterLabels?.unverified || 'Unverified';
      parts.push(filterType === 'verified' ? verifiedLabel : unverifiedLabel);
    }
    
    if (sourceFilter !== 'all') {
      parts.push(`Source: ${sourceFilter}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'All findings';
  };

  return {
    // Filter state
    filterType,
    sourceFilter,
    showSourceDropdown,
    setShowSourceDropdown,
    filterOptions,
    setFilterOptions,
    
    // Filter actions
    handleFilterChange,
    handleSourceFilterChange,
    fetchFilterOptions,
    resetFilters,
    
    // Filter utilities
    buildFilterRequest,
    applyClientSideFilters,
    getUniqueSourcesFromData,
    validateSourceFilter,
    getSourceCount,
    getFilterCounts,
    hasActiveFilters,
    getFilterSummary
  };
};